import React, { useMemo, useState } from "react";

// Shared column-sorting helpers for every table in the Revenue Tracker
// (revenue-by-bill, negative margins, outstanding bills). One
// implementation so a comparator fix lands everywhere at once.
//
// A column descriptor looks like:
//   { key: "amount", label: "Amount", type: "number", numeric: true }
// `type` drives the comparator ("number" | "date" | "string").
// `numeric` only controls right-alignment + the default sort direction
// when that column is first clicked.

export function compareRows(a, b, sortKey, sortDir, colType) {
  const av = a?.[sortKey];
  const bv = b?.[sortKey];
  // Nulls / blanks always sink to the bottom regardless of direction, so
  // an ascending sort on Domain doesn't lead with a wall of "—" rows.
  const aMissing = av == null || av === "";
  const bMissing = bv == null || bv === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp;
  if (colType === "number") {
    cmp = Number(av) - Number(bv);
  } else if (colType === "date") {
    cmp = new Date(av).getTime() - new Date(bv).getTime();
  } else {
    cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
  }
  return sortDir === "asc" ? cmp : -cmp;
}

/**
 * Sorting state + a pre-sorted copy of `rows`.
 *
 * Clicking the active column toggles asc/desc. Clicking a different
 * column jumps to that column's most-useful default — descending for
 * numeric columns (biggest first), ascending for everything else.
 */
export function useSortableRows(rows, columns, defaultKey, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (col) => {
    if (col.key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir(col.numeric ? "desc" : "asc");
    }
  };

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    const type = col?.type || "string";
    // Slice first — never sort the caller's array in place, or a
    // re-render can re-sort mid-flight and land on the wrong order.
    return [...(rows || [])].sort((a, b) =>
      compareRows(a, b, sortKey, sortDir, type),
    );
  }, [rows, columns, sortKey, sortDir]);

  return { sortKey, sortDir, sortedRows, handleSort };
}

/** Clickable header cell with an active/inactive sort arrow. */
export function SortableTh({ col, sortKey, sortDir, onSort, style }) {
  const active = col.key === sortKey;
  const arrow = !active ? "↕" : sortDir === "asc" ? "↑" : "↓";
  return (
    <th
      onClick={() => onSort(col)}
      title={`Sort by ${col.label}`}
      style={{
        cursor: "pointer",
        userSelect: "none",
        textAlign: col.numeric ? "right" : "left",
        color: active ? "#134f8b" : undefined,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {col.label}
      <span
        style={{
          fontSize: "0.75em",
          marginLeft: 4,
          color: active ? "#134f8b" : "#8696aa",
        }}
      >
        {arrow}
      </span>
    </th>
  );
}

/** Renders a full <tr> of sortable headers from a column list. */
export function SortableHeaderRow({ columns, sortKey, sortDir, onSort }) {
  return (
    <tr>
      {columns.map((col) => (
        <SortableTh
          key={col.key}
          col={col}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
        />
      ))}
    </tr>
  );
}
